// Don't import anything from open layers in this component
// We want to keep the open layers dependency in a separate chunk
// This is to avoid loading open layers on the initial page load

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { StaticRenderSuspence } from '@/components/staticRenderSuspence';
import type { Projection } from '@/components/maps/openlayers/projections';
import useBelow from '@/hooks/useBelow';
import { useLockBodyScroll } from '@/hooks/useLockBodyScroll';
import { useOnClickOutside } from '@/hooks/useOnClickOutside';
import { useDynamicNavigate } from '@/reactRouterPlugins/dynamicLink';
import { BoundingBox } from '@/types';
import { cn } from '@/utils/shadcn';
import { lazy, useEffect, useRef, useState } from 'react';
import { RasterStyles } from '../options';
import { parseStoredStyle, resolveInitialStyle, serializeStyle } from './styleStorage';
import { BasisOfRecordFilter } from './controls/basisOfRecordFilter';
import { ClickToExploreAreaButton } from './controls/clickToExploreAreaButton';
import { ExploreLink } from './controls/exploreLink';
import { ProjectionSelector } from './controls/projectionSelector';
import { StyleSelector } from './controls/styleSelector/styleSelector';
import { YearFilter } from './controls/yearFilter';
import { useCapabilities } from './useCapabilities';
import { useFilterParams } from './useFilterParams';

const MapWidgetInner = lazy(() => import('../inner'));

const defaultPixelMapParams = {
  hasCoordinate: [true],
  hasGeospatialIssue: [false],
  occurrenceStatus: ['PRESENT'],
};

type Props = {
  className?: string;
  capabilitiesParams?: Record<string, any>;
  mapStyle?: string;
  defaultStyleName?: string;
  persistStyleSelection?: boolean;
};

const defaultCapabilitiesParams = {}; // Stable reference as fallback prop to prevent cascading rerenders without any real change

const STORAGE_KEY = 'mapWidget.selectedStyle';

export function MapWidgetOuter({
  className,
  capabilitiesParams = defaultCapabilitiesParams,
  mapStyle,
  defaultStyleName = 'GREEN',
  persistStyleSelection = false,
}: Props) {
  const isSmallScreen = useBelow(800, true);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const toggleFullScreen = () => setIsFullScreen((current) => !current);

  const [selectedProjection, setSelectedProjection] = useState<Projection>('EPSG_4326');

  // Get initial style from localStorage if persistence is enabled, otherwise use props/defaults
  const getInitialStyle = (): RasterStyles => {
    let stored: RasterStyles | null = null;
    if (persistStyleSelection && typeof window !== 'undefined') {
      try {
        stored = parseStoredStyle(localStorage.getItem(STORAGE_KEY));
      } catch (e) {
        // Ignore localStorage errors
      }
    }

    return resolveInitialStyle({ mapStyle, persistStyleSelection, defaultStyleName, stored });
  };

  const [rasterStyles, setRasterStyles] = useState<RasterStyles>(getInitialStyle);

  const [basisOfRecord, setBasisOfRecord] = useState<string[]>([]);

  // Year filter state
  const [isYearFilterActive, setIsYearFilterActive] = useState(false);
  const [startYear, setStartYear] = useState<number>();
  const [endYear, setEndYear] = useState<number>();

  // The bounding box of the area that is currently visible on the map
  const [boundingBox, setBoundingBox] = useState<BoundingBox>();
  const [view, setView] = useState<{ center: [number, number]; zoom: number }>();

  const [clickToSearchAreaEnabled, setClickToSearchAreaEnabled] = useState(false);
  // Disable the click to explore funciton if clicking outside the map
  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, () => setClickToSearchAreaEnabled(false));

  // Create a params object based on all the state filters
  const filterParams = useFilterParams(
    basisOfRecord,
    isYearFilterActive,
    startYear,
    endYear,
    capabilitiesParams
  );

  const dynamicNavigate = useDynamicNavigate();

  const { data: capabilities } = useCapabilities({ capabilitiesParams });

  useEffect(() => {
    if (!!capabilities && (capabilitiesParams?.taxonKey || capabilitiesParams?.country)) {
      setBoundingBox({
        top: capabilities.minLat,
        left: capabilities.maxLat,
        bottom: capabilities.minLng,
        right: capabilities.maxLng,
      });
    }
  }, [capabilities, capabilitiesParams?.taxonKey, capabilitiesParams?.country]);

  // Update the start and end year based on the capabilities
  useEffect(() => {
    if (capabilities?.minYear) {
      setStartYear((current) => Math.max(current ?? 0, capabilities.minYear));
    }
  }, [capabilities?.minYear]);
  useEffect(() => {
    if (capabilities?.maxYear) {
      setEndYear((current) => Math.max(current ?? 0, capabilities.maxYear));
    }
  }, [capabilities?.maxYear]);

  useLockBodyScroll(isFullScreen);

  // Persist style selection to localStorage when it changes (only if persistence is enabled)
  useEffect(() => {
    if (persistStyleSelection && typeof window !== 'undefined') {
      try {
        // Persist the full style so custom styles (name + baseMapStyle + params) survive too
        localStorage.setItem(STORAGE_KEY, serializeStyle(rasterStyles));
      } catch (e) {
        // Ignore localStorage errors
      }
    }
  }, [rasterStyles, persistStyleSelection]);

  let yearFilter: React.ReactNode = <div />;
  if (
    typeof capabilities?.minYear === 'number' &&
    typeof capabilities?.maxYear === 'number' &&
    capabilities.minYear !== capabilities.maxYear
  ) {
    yearFilter = (
      <YearFilter
        isYearFilterActive={isYearFilterActive}
        setIsYearFilterActive={setIsYearFilterActive}
        startYear={startYear}
        endYear={endYear}
        setStartYear={setStartYear}
        setEndYear={setEndYear}
        lowerLimit={capabilities.minYear}
        upperLimit={capabilities.maxYear}
      />
    );
  }

  return (
    <div className={cn('g-w-full', className)}>
      {/* Placeholder with the same size as the map+controls that will prevent the page from hopping up and down when entering and exiting the full screen mode */}
      {isFullScreen && <div className="g-aspect-[2] g-min-h-[300px]  g-w-full g-mb-12" />}
      <div
        className={cn('g-flex g-flex-col g-w-full', {
          'g-fixed g-z-50 g-top-0 g-start-0 g-h-screen': isFullScreen,
        })}
      >
        <div className="g-flex-1 g-w-full g-aspect-[2] g-min-h-[300px] g-bg-gray-100 g-border-t">
          <ErrorBoundary type="BLOCK" errorMessage="Failed to load map">
            <div ref={ref} className="g-w-full g-h-full g-aspect-[2] g-min-h-[300px]">
              <StaticRenderSuspence>
                <MapWidgetInner
                  className="g-w-full g-h-full"
                  isFullSize={isFullScreen}
                  toggleFullScreen={toggleFullScreen}
                  generatedAt={capabilities?.generated}
                  capabilities={capabilities}
                  filterParams={filterParams}
                  selectedProjection={selectedProjection}
                  rasterStyles={rasterStyles}
                  setBoundingBox={setBoundingBox}
                  setView={setView}
                  enabledSearchAreaClick={clickToSearchAreaEnabled}
                  onSearchAreaClick={(geometryFilter) => {
                    dynamicNavigate({
                      pageId: 'occurrenceSearch',
                      searchParams: {
                        ...filterParams,
                        geometry: geometryFilter,
                        ...defaultPixelMapParams,
                        view: 'map',
                      },
                    });
                  }}
                />
              </StaticRenderSuspence>
            </div>
          </ErrorBoundary>
        </div>
        <div className="g-bg-white g-w-full g-flex g-justify-between g-px-2 g-border-t g-h-12">
          {isSmallScreen ? <div /> : yearFilter}
          <div className="g-flex g-items-center g-gap-2">
            <ExploreLink
              className="g-pe-2"
              boundingBox={boundingBox}
              view={view}
              projection={selectedProjection}
              filterParams={{ ...filterParams, ...defaultPixelMapParams, view: 'map' }}
            />
            <BasisOfRecordFilter
              selected={basisOfRecord}
              setSelected={setBasisOfRecord}
              yearFilter={isSmallScreen ? yearFilter : undefined}
            />
            <StyleSelector rasterStyles={rasterStyles} setRasterStyles={setRasterStyles} />
            <ProjectionSelector
              selectedProjection={selectedProjection}
              setSelectedProjection={setSelectedProjection}
            />
            <ClickToExploreAreaButton
              clickToSearchAreaEnabled={clickToSearchAreaEnabled}
              setClickToSearchAreaEnabled={setClickToSearchAreaEnabled}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
